import React from 'react';
import { isStartMonth, startLine, startTooltip } from './startMonth';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const STYLE = {
  reconciled: { cls: 'bg-green-100 text-green-800', label: '✓' },
  in_progress: { cls: 'bg-yellow-100 text-yellow-800', label: '…' },
  missing: { cls: 'bg-red-100 text-red-700', label: '!' },
  opening: { cls: 'bg-blue-50 text-blue-700', label: 'S' },
  none: { cls: 'bg-gray-50 text-gray-300', label: '·' }
};
// Any month that is not reconciled but shows a status mark (!, …, or a new
// status added later) gets a light yellow tint and a bold red mark, so open
// months never look like the reconciled check-mark cells. No dark: classes:
// the app has no dark theme, and Tailwind's default dark mode follows the
// computer's setting, which turned these cells brown on a light page.
// A reconciled month keeps its own colour, the opening (start) month stays
// blue as STYLE defines, and an empty month stays grey.
const NOT_RECONCILED = 'bg-yellow-100 text-red-600 font-extrabold text-base';
const OWN_COLOUR = new Set(['reconciled', 'opening', 'none']);
export function cellStyle(status) {
  const s = STYLE[status] || STYLE.none;
  if (OWN_COLOUR.has(status) || !STYLE[status]) return s;
  return { ...s, cls: NOT_RECONCILED, notReconciled: true };
}
const TITLE = { reconciled: 'Reconciled', in_progress: 'In progress', missing: 'Not reconciled (gap)', opening: 'Opening month (starting balance)', none: 'Nothing expected yet' };

// A month is inside closed books when its last day is on or before the
// business closing date (Close the books, 20261206).
export function isClosedMonth(month, closedThrough) {
  if (!closedThrough || !month) return false;
  const [y, m] = month.split('-').map(Number);
  const last = new Date(Date.UTC(y, m, 0)).toISOString().slice(0, 10);
  return last <= closedThrough;
}

// Account x month grid. Clicking a cell opens that month.
export default function MonthlyStatusGrid({ accounts, selected, onOpen, closedThrough = null }) {
  if (!accounts.length) return <p className="text-gray-500">No accounts for this selection.</p>;
  return (
    <div className="overflow-x-auto bg-white rounded shadow">
      <table className="min-w-full text-sm">
        <thead>
          <tr className="bg-gray-50">
            <th className="text-left px-3 py-2">Account</th>
            {MONTHS.map((m) => <th key={m} className="px-2 py-2 text-center">{m}</th>)}
            <th className="px-3 py-2 text-center">Gaps</th>
          </tr>
        </thead>
        <tbody>
          {accounts.map((a) => (
            <tr key={a.account_id} className="border-t">
              <td className="px-3 py-2">
                <div className="font-medium">{a.account_name}</div>
                <div className="text-xs text-gray-500">{a.account_type}{a.is_liability ? ' · liability' : ''}</div>
                {startLine(a) && <div className="text-xs text-blue-700" data-testid="start-line">{startLine(a)}</div>}
              </td>
              {a.months.map((m) => {
                const s = cellStyle(m.status);
                const isStart = isStartMonth(a, m.month);
                const isSel = selected && selected.accountId === a.account_id && selected.month === m.month;
                const closed = isClosedMonth(m.month, closedThrough);
                return (
                  <td key={m.month} className="px-1 py-1 text-center">
                    <button
                      type="button"
                      aria-label={s.notReconciled ? 'Not reconciled' : undefined}
                      title={`${closed ? 'Books closed: ' : ''}${a.account_name} · ${m.month.slice(0, 7)} · ${s.notReconciled ? 'Not reconciled · ' : ''}${TITLE[m.status]}${isStart ? startTooltip(a) : ''}`}
                      onClick={() => onOpen(a, m.month)}
                      className={`relative w-8 h-8 rounded ${s.cls} ${isSel ? 'ring-2 ring-blue-500' : ''} ${closed ? 'outline outline-2 outline-gray-700' : ''}`}
                      data-closed={closed ? 'true' : undefined}
                    >
                      {s.label}
                      {closed && (
                        <span className="absolute -bottom-1 -right-1 rounded bg-gray-700 text-white text-[8px] leading-none px-0.5 py-0.5" data-testid="closed-badge" aria-label="Books closed">{'\u{1F512}'}</span>
                      )}
                      {isStart && (
                        <span className="absolute -top-1 -right-1 rounded-full bg-blue-600 text-white text-[9px] leading-none px-1 py-0.5" data-testid="start-badge">S</span>
                      )}
                    </button>
                  </td>
                );
              })}
              <td className={`px-3 py-2 text-center ${a.gaps ? 'text-red-700 font-semibold' : 'text-gray-400'}`}>{a.gaps}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className="text-xs text-gray-500 px-3 py-2">✓ reconciled · … in progress · ! month ended and not reconciled · S opening month · <span className="inline-block rounded-full bg-blue-600 text-white text-[9px] leading-none px-1 py-0.5">S</span> start month (starting balance) · yellow with a bold red ! = not reconciled yet</p>
    </div>
  );
}
